export type ClientPreviewTourCandidateId =
  | 'balances'
  | 'review-document'
  | 'estimated-payments'
  | 'signature';

export type ClientPreviewTourCandidate = {
  id: ClientPreviewTourCandidateId;
  targetId: string;
  stepIndex: number;
  itemIndex: number;
  title: string;
  body: string;
};

export type ClientPreviewTourNavigateSignal = {
  signalId: number;
  candidate: ClientPreviewTourCandidate;
};

type ClientPreviewTourItem = {
  prompt: string;
  itemIndex: number;
  stepIndex: number;
  itemType: string;
  dependencyType: string | null;
  dependencyObject: unknown;
  isCustomStep?: boolean;
};

type ClientPreviewTourStep = {
  items: ClientPreviewTourItem[];
};

type CandidateDefinition = {
  id: ClientPreviewTourCandidateId;
  targetId: string;
  title: string;
  body: string;
  matches: (item: ClientPreviewTourItem) => boolean;
};

const normalize = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const dependencyObjectType = (item: ClientPreviewTourItem) =>
  normalize((item.dependencyObject as { type?: unknown } | null)?.type);

const candidateDefinitions: CandidateDefinition[] = [
  {
    id: 'balances',
    targetId: 'tax-delivery-client-preview-balances',
    title: 'Review payment and refund details',
    body: 'Clients see balances and refunds organized clearly before they complete the delivery.',
    matches: (item) => {
      const label = normalize(item.prompt);
      const dependencyType = normalize(item.dependencyType);
      const dynamicType = dependencyObjectType(item);
      return (
        dynamicType === 'balances' ||
        dependencyType === 'balances' ||
        label.includes('balances due') ||
        label.includes('refund')
      );
    },
  },
  {
    id: 'review-document',
    targetId: 'tax-delivery-client-preview-review-document',
    title: 'Review included tax documents',
    body: 'Clients can review the return documents you include before completing delivery.',
    matches: (item) => {
      const label = normalize(item.prompt);
      const itemType = normalize(item.itemType);
      return itemType === 'reviewable_document' || label.startsWith('review ');
    },
  },
  {
    id: 'estimated-payments',
    targetId: 'tax-delivery-client-preview-estimated-payments',
    title: 'Review estimated payments',
    body: 'Estimated payments are presented as part of the client journey so clients know what is due and when.',
    matches: (item) => {
      const label = normalize(item.prompt);
      const dependencyType = normalize(item.dependencyType);
      const dynamicType = dependencyObjectType(item);
      return (
        dynamicType === 'payment_estimates' ||
        dependencyType === 'payment_estimates' ||
        label.includes('estimated payments')
      );
    },
  },
  {
    id: 'signature',
    targetId: 'tax-delivery-client-preview-signature',
    title: 'Review the signing step',
    body: 'Clients can complete signing as part of the same guided delivery flow.',
    matches: (item) => {
      const label = normalize(item.prompt);
      const itemType = normalize(item.itemType);
      return itemType === 'signable_document' || label.includes('signature');
    },
  },
];

const findFirstMatch = (
  steps: ClientPreviewTourStep[],
  definition: CandidateDefinition
): ClientPreviewTourCandidate | null => {
  for (const step of steps) {
    for (const item of step.items) {
      if (item.isCustomStep === true) continue;
      if (!definition.matches(item)) continue;

      return {
        id: definition.id,
        targetId: definition.targetId,
        stepIndex: item.stepIndex,
        itemIndex: item.itemIndex,
        title: definition.title,
        body: definition.body,
      };
    }
  }

  return null;
};

export const selectClientPreviewTourCandidates = (
  steps: ClientPreviewTourStep[]
): ClientPreviewTourCandidate[] => {
  const byId = new Map<ClientPreviewTourCandidateId, ClientPreviewTourCandidate>();

  for (const definition of candidateDefinitions) {
    const match = findFirstMatch(steps, definition);
    if (match) byId.set(definition.id, match);
  }

  const selected = [byId.get('balances')].filter(
    Boolean
  ) as ClientPreviewTourCandidate[];

  const signatureOrReviewDocument = byId.get('signature') ?? byId.get('review-document');
  if (signatureOrReviewDocument) selected.push(signatureOrReviewDocument);

  const estimatedPayments = byId.get('estimated-payments');
  if (estimatedPayments) selected.push(estimatedPayments);

  return selected.slice(0, 3);
};
